import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Key, Users, CreditCard, RefreshCw, MessageSquare, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ONBOARDING_KEY = 'iptv4kfr_client_onboarding_done';

const steps = [
  {
    icon: <Key className="h-8 w-8 text-[#FF6B35]" />,
    title: "Vos identifiants IPTV",
    desc: "Retrouvez vos identifiants de connexion (username, password, URL serveur) directement dans votre espace client. Ils sont aussi envoy\u00e9s par email.",
    highlight: '[data-testid="credentials-section"], [data-testid="client-credentials"]',
    position: 'center'
  },
  {
    icon: <Users className="h-8 w-8 text-[#00FF88]" />,
    title: "Le parrainage",
    desc: "Partagez votre lien de parrainage unique. Pour chaque ami inscrit, vous recevez 10\u20ac de cr\u00e9dit. Vos amis b\u00e9n\u00e9ficient aussi d'une r\u00e9duction de 10\u20ac.",
    highlight: '[data-testid="referral-section"]',
    position: 'center'
  },
  {
    icon: <CreditCard className="h-8 w-8 text-[#FFB800]" />,
    title: "Vos cr\u00e9dits",
    desc: "Consultez votre solde de cr\u00e9dit dans votre tableau de bord. Les cr\u00e9dits sont automatiquement d\u00e9duits de votre prochain renouvellement.",
    highlight: '[data-testid="credit-section"], [data-testid="credit-balance"]',
    position: 'center'
  },
  {
    icon: <RefreshCw className="h-8 w-8 text-[#00FFFF]" />,
    title: "Renouveler votre abonnement",
    desc: "Lorsque votre abonnement approche de son expiration, un bouton de renouvellement appara\u00eet. Vos cr\u00e9dits sont appliqu\u00e9s automatiquement.",
    highlight: '[data-testid="subscription-section"]',
    position: 'center'
  },
  {
    icon: <MessageSquare className="h-8 w-8 text-[#F7931E]" />,
    title: "Contacter le support",
    desc: "Besoin d'aide ? Ouvrez un ticket depuis la rubrique Support de votre espace client. Notre \u00e9quipe vous r\u00e9pond sous 24h.",
    highlight: '[data-testid="support-link"]',
    position: 'center'
  },
  {
    icon: <Star className="h-8 w-8 text-[#FFB800]" />,
    title: "Laissez un avis",
    desc: "Votre avis compte ! En bas de votre tableau de bord, vous pouvez noter et commenter notre service. Les meilleurs avis sont affich\u00e9s sur notre page d'accueil.",
    highlight: '[data-testid="review-section"]',
    position: 'center'
  }
];

const ClientOnboarding = () => {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (!done) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleClose = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setVisible(false);
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const step = steps[currentStep];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100]" data-testid="client-onboarding">
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

        {/* Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md px-4"
        >
          <div className="bg-[#1A1A1D] rounded-xl border border-[#FF6B35]/30 shadow-2xl shadow-[#FF6B35]/10 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-white text-sm font-medium">Guide de d&eacute;marrage</span>
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                  {currentStep + 1}/{steps.length}
                </span>
              </div>
              <button onClick={handleClose} className="text-white/80 hover:text-white" data-testid="close-onboarding">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Step Content */}
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 rounded-xl bg-[#222226] border border-[#3A3A3D]">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-white">{step.title}</h3>
              </div>
              <p className="text-[#B8B8BA] text-sm leading-relaxed mb-6">{step.desc}</p>

              {/* Progress dots */}
              <div className="flex items-center justify-center space-x-2 mb-6">
                {steps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === currentStep ? 'w-6 bg-[#FF6B35]' : i < currentStep ? 'w-1.5 bg-[#FF6B35]/50' : 'w-1.5 bg-[#3A3A3D]'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleSkip}
                  className="text-[#6B6B6E] hover:text-white text-sm transition-colors"
                  data-testid="skip-onboarding"
                >
                  Passer
                </button>
                <div className="flex items-center space-x-2">
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrev}
                      className="p-2 rounded-lg bg-[#222226] border border-[#3A3A3D] text-white hover:border-[#FF6B35] transition-colors"
                      data-testid="prev-step"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={handleNext}
                    className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-medium text-sm hover:shadow-lg hover:shadow-[#FF6B35]/30 transition-all flex items-center space-x-1"
                    data-testid="next-step"
                  >
                    <span>{currentStep === steps.length - 1 ? "C'est parti !" : 'Suivant'}</span>
                    {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export { ONBOARDING_KEY };
export default ClientOnboarding;
